// ** React Imports
import { ReactNode, ReactElement, useEffect } from 'react'

// ** Next Import
import { NextRouter, useRouter } from 'next/router'

// ** Hooks Import
import { useAuth } from 'src/hooks/useAuth'
import { AuthValuesType } from '@/src/context/types'

// ** Utils Import
import { ROUTES } from '@/src/utils/routes'

interface AuthGuardProps {
  children: ReactNode
  fallback: ReactElement | null
}

async function handleAuth(auth: AuthValuesType, router: NextRouter) {
  if (!router.isReady) {
    return
  }

  // Don't redirect if we're on error pages or login page to prevent loops
  const errorPages = [ROUTES.NOT_FOUND, ROUTES.SERVER_ERROR, ROUTES.UNAUTHORIZED, ROUTES.LOGIN]
  const isErrorPage = errorPages.some(page => router.asPath === page || router.pathname === page)

  if (isErrorPage) {
    return
  }

  if (auth.user === null) {
    if (router.asPath !== ROUTES.HOME) {
      await router.replace({
        pathname: ROUTES.LOGIN,
        query: { returnUrl: router.asPath }
      })
    } else {
      await router.replace(ROUTES.LOGIN)
    }
  }
}

const AuthGuard = (props: AuthGuardProps) => {
  const { children, fallback } = props
  const auth = useAuth()
  const router = useRouter()

  useEffect(
    () => {
      handleAuth(auth, router)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router]
  )

  if (auth.loading || auth.user === null) {
    return fallback
  }

  return <>{children}</>
}

export default AuthGuard
